import { DocumentPreview } from "components/elements/DocumentDownload";
import Typography, { ITypo } from "components/elements/Typography";
import classes from "./classes.module.scss";
import { matchPath, useLocation, useNavigate, useOutletContext, useParams } from "react-router-dom";

import { container } from "tsyringe";
import ProductSubscribedService from "services/ProductSubscribedService";
import SubscriptionService from "services/SubscriptionService";
import { useCallback, useEffect, useState } from "react";
import Form from "components/materials/Form";
import Button, { EButtonVariant } from "components/elements/Button";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import I18n from "components/materials/I18n";
import { ISubscriptionOutletContext } from "..";
import Modal from "components/elements/Modal";
import MessageBox from "components/elements/MessageBox";

const subscriptionService = container.resolve(SubscriptionService);
const productSubscribedService = container.resolve(ProductSubscribedService);

export default function ValidateProductDocumentsCorporation() {
	const { subscriptionId } = useParams();
	const { documentValidationSteps, loadSubscription } = useOutletContext<ISubscriptionOutletContext>();
	const navigate = useNavigate();
	const location = useLocation();

	const [isReportModalOpen, setIsReportModalOpen] = useState(false);
	const [errorMessage, setErrorMessage] = useState("");

	const currentStepIndex = documentValidationSteps.findIndex((step) => matchPath(step.path, location.pathname));
	const nextStep = documentValidationSteps[currentStepIndex + 1];

	// Private equity : aucune convention RTO, on passe directement aux documents du produit.
	const firstStepPath = documentValidationSteps[0]?.path;
	useEffect(() => {
		if (currentStepIndex === -1 && firstStepPath) navigate(firstStepPath, { replace: true });
	}, [currentStepIndex, firstStepPath, navigate]);

	const [productSubscribedId, setProductSubscribedId] = useState<string | null>(null);

	useEffect(() => {
		subscriptionService.getByUid(subscriptionId ?? "").then((subscription) => {
			if (!subscription) return;
			setProductSubscribedId(subscription.productsSubscribed?.[0]?.id ?? null);
		});
	}, [subscriptionId]);

	const handleValidateStep = useCallback(async () => {
		if (!subscriptionId) return;

		try {
			await subscriptionService.validateRtoDocument(subscriptionId);
			loadSubscription();
			if (nextStep) {
				navigate(nextStep.path);
			}
		} catch (error) {
			console.error("Erreur lors de la validation :", error);
		}
	}, [subscriptionId, loadSubscription, nextStep, navigate]);

	const handleSubmitAnomaly = useCallback(async () => {
		if (!subscriptionId || !errorMessage.trim()) return;

		try {
			await subscriptionService.reportAnomaly(subscriptionId, {
				message: errorMessage.trim(),
			});
			loadSubscription();
			setIsReportModalOpen(false);
			setErrorMessage("");

			if (nextStep) {
				navigate(nextStep.path);
			} else {
				// Possibilité d’afficher un message de confirmation ?
			}
		} catch (err) {
			console.error("Erreur lors de la soumission :", err);
		}
	}, [subscriptionId, errorMessage, loadSubscription, nextStep, navigate]);

	return (
		<div className={classes["root"]}>
			<Typography typo={ITypo.P_MEDIUM} className={classes["subtitle"]}>
				Vous pouvez dès-à-présent vérifier les documents avant leur envoi en signature électronique.
			</Typography>
			<Typography typo={ITypo.H2} className={classes["subtitle"]}>
				Convention RTO
			</Typography>
			<section className={classes["pdf-container"]}>{productSubscribedId && <DocumentPreview url={getRtoAction(productSubscribedId)} />}</section>
			<div className={classes["footer"]}>
				<Button onClick={handleValidateStep}>{I18n.asset.common.validate}</Button>
				<Button icon={<ExclamationTriangleIcon />} iconposition="left" variant={EButtonVariant.OUTLINED} onClick={() => setIsReportModalOpen(true)}>
					{I18n.asset.common.report_anomaly}
				</Button>
			</div>
			<Modal isOpen={isReportModalOpen} onClose={() => setIsReportModalOpen(false)} className={classes["modal-content"]}>
				<Typography typo={ITypo.H1} className={classes["modal-title"]}>
					{I18n.asset.pages.subscriptions.report_error_modal.title}
				</Typography>
				<Form className={classes["modal-form"]} onSubmit={(e) => e.preventDefault()}>
					<textarea
						className={classes["modal-textarea"]}
						name="errorMessage"
						placeholder={I18n.asset.pages.subscriptions.report_error_modal.placeholder}
						value={errorMessage}
						onChange={(e) => setErrorMessage(e.target.value)}
					/>
					<MessageBox className={classes["message-content"]} type="info" text={I18n.asset.pages.subscriptions.report_error_modal.message} />

					<div className={classes["modal-button"]}>
						<Button onClick={() => setIsReportModalOpen(false)} variant={EButtonVariant.OUTLINED}>
							{I18n.asset.common.cancel}
						</Button>
						<Button onClick={handleSubmitAnomaly}>{I18n.asset.common.send}</Button>
					</div>
				</Form>
			</Modal>
		</div>
	);
}

function getRtoAction(productSubscribedId: string) {
	return productSubscribedService.buildRtoSignableDocumentUrl(productSubscribedId);
}
